import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Copy, Package, Printer, Share2, Truck, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import Reveal from "@/components/Reveal";

const TRACKER = ["Order confirmed", "Packed in the vault", "Shipped", "Out for delivery"];

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { clear, paymentsMode } = useCart();
  const sessionId = params.get("session_id");
  const [order, setOrder] = useState(null);
  const [referral, setReferral] = useState(null);
  const [phase, setPhase] = useState(sessionId ? "confirming" : "error");
  const [tries, setTries] = useState(0);
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      navigate("/", { replace: true });
      return;
    }
    let stop = false;
    (async () => {
      while (!stop) {
        try {
          const { data } = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/status/${sessionId}`).then((r) => r.json());
          if (data.payment_status === "paid") {
            setOrder(data.order);
            setPhase("paid");
            if (!clearedRef.current) {
              clearedRef.current = true;
              clear();
              localStorage.removeItem("voltaic_abandoned");
              try {
                const ids = JSON.parse(localStorage.getItem("voltaic_orders") || "[]");
                const next = [...new Set([data.order?.order_id, ...ids])].filter(Boolean).slice(0, 20);
                localStorage.setItem("voltaic_orders", JSON.stringify(next));
              } catch {}
              if (data.order?.customer?.email) {
                api.post("/referrals/code", { email: data.order.customer.email })
                  .then((r) => setReferral(r.data))
                  .catch(() => {});
              }
            }
            return;
          }
        } catch {
          /* keep polling */
        }
        setTries((t) => t + 1);
        if (stop) return;
        await new Promise((r) => setTimeout(r, 2500));
      }
    })();
    return () => {
      stop = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(referral.code);
      toast.success("Referral code copied");
    } catch {
      /* clipboard unavailable */
    }
  };

  if (phase === "confirming") {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-6 px-6 text-center" data-testid="payment-confirming">
        <motion.div
          animate={{ scale: [1, 1.15, 1], opacity: [1, 0.6, 1] }}
          transition={{ duration: 1.6, repeat: Infinity }}
          className="grid h-20 w-20 place-items-center rounded-full border-2 border-voltaic-cyan/40"
        >
          <span className="h-10 w-10 animate-spin rounded-full border-2 border-voltaic-cyan/30 border-t-voltaic-cyan" />
        </motion.div>
        <div>
          <h1 className="font-display text-2xl font-extrabold uppercase">Confirming your payment</h1>
          <p className="mt-2 text-sm text-voltaic-muted">Verifying with Stripe — this takes a few seconds{tries > 6 ? " (almost there)" : ""}.</p>
        </div>
      </main>
    );
  }

  if (phase === "error") {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-5 px-6 text-center" data-testid="payment-error">
        <XCircle size={48} className="text-voltaic-pink" />
        <h1 className="font-display text-2xl font-extrabold uppercase">Missing payment session</h1>
        <p className="text-sm text-voltaic-muted">We couldn't find a Stripe session to verify.</p>
        <Link to="/shop" data-testid="payment-error-back" className="rounded-full bg-voltaic-cyan px-7 py-3 font-semibold text-voltaic-bg">Back to shop</Link>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-3xl px-5 pb-24 pt-36 lg:px-10" data-testid="order-confirmation">
      <Reveal>
        <div className="flex flex-col items-center text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 220, damping: 14 }}
            className="grid h-20 w-20 place-items-center rounded-full bg-voltaic-green/10 text-voltaic-green"
          >
            <CheckCircle2 size={40} />
          </motion.div>
          <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.3em] text-voltaic-green">Payment verified // {paymentsMode === "live" ? "Live payment" : "Stripe test"}</p>
          <h1 className="mt-2 font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl">You're charged up.</h1>
          <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-voltaic-muted" data-testid="order-number">
            Order {order?.order_id}
          </p>
        </div>
      </Reveal>

      {/* Tracker */}
      <Reveal delay={0.1}>
        <div className="mt-12 rounded-3xl border border-voltaic-line bg-voltaic-surface/60 p-6 sm:p-8" data-testid="order-tracker">
          <div className="flex items-start justify-between">
            {TRACKER.map((step, i) => {
              const doneUpTo = order?.status === "shipped" ? 2 : 0;
              const active = i <= doneUpTo;
              return (
              <div key={step} className="flex flex-1 flex-col items-center gap-3">
                <div className="flex w-full items-center">
                  <div className={`h-0.5 flex-1 ${i === 0 ? "opacity-0" : i <= doneUpTo + 1 ? "bg-voltaic-green" : "bg-voltaic-line"}`} />
                  <span
                    className={`grid h-9 w-9 shrink-0 place-items-center rounded-full border text-xs font-bold ${
                      active ? "border-voltaic-green bg-voltaic-green/10 text-voltaic-green" : "border-voltaic-line text-voltaic-muted"
                    }`}
                  >
                    {i === 0 || i === 2 ? <CheckCircle2 size={16} /> : i === 1 ? <Package size={15} /> : <Truck size={15} />}
                  </span>
                  <div className={`h-0.5 flex-1 ${i === TRACKER.length - 1 ? "opacity-0" : i < doneUpTo + 1 ? "bg-voltaic-green" : "bg-voltaic-line"}`} />
                </div>
                <span className={`text-center font-mono text-[9px] uppercase tracking-[0.15em] ${active ? "text-voltaic-green" : "text-voltaic-muted"}`}>{step}</span>
              </div>
              );
            })}
          </div>
          {order?.status === "shipped" && (
            <p className="mt-5 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-voltaic-cyan" data-testid="tracker-shipped-note">
              Your order has shipped
            </p>
          )}
        </div>
      </Reveal>

      {/* Receipt */}
      <Reveal delay={0.15}>
        <div className="mt-6 rounded-3xl border border-voltaic-line bg-voltaic-surface/60 p-6 sm:p-8" data-testid="order-receipt">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-bold uppercase tracking-wide">Receipt</h2>
            <button
              data-testid="download-receipt-button"
              onClick={() => window.print()}
              className="flex items-center gap-2 rounded-full border border-voltaic-line px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-voltaic-muted transition-colors hover:border-voltaic-cyan hover:text-voltaic-cyan"
            >
              <Printer size={13} /> Save PDF
            </button>
          </div>

          <div className="mt-5 space-y-3">
            {order?.items?.map((i) => (
              <div key={i.product_id} className="flex items-center gap-3" data-testid={`receipt-item-${i.product_id}`}>
                <img src={i.image} alt={i.name} className="h-12 w-12 rounded-lg object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{i.name}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-voltaic-muted">Qty {i.quantity}</p>
                </div>
                <p className="text-sm font-bold">{money(i.price * i.quantity)}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 space-y-1.5 border-t border-voltaic-line pt-5 text-sm">
            <div className="flex justify-between text-voltaic-muted"><span>Subtotal</span><span>{money(order?.subtotal || 0)}</span></div>
            {order?.discount > 0 && (
              <div className="flex justify-between text-voltaic-green"><span>Promo {order.promo_code}</span><span>-{money(order.discount)}</span></div>
            )}
            <div className="flex justify-between text-voltaic-muted"><span>Shipping</span><span>{order?.shipping === 0 ? "Free" : money(order?.shipping || 0)}</span></div>
            <div className="flex justify-between pt-2 font-display text-lg font-extrabold"><span>Total paid</span><span data-testid="receipt-total">{money(order?.subtotal - order?.discount + (order?.shipping || 0))}</span></div>
          </div>

          <div className="mt-6 rounded-2xl border border-voltaic-line bg-voltaic-bg/50 p-4 font-mono text-[10px] uppercase leading-relaxed tracking-[0.15em] text-voltaic-muted" data-testid="receipt-address">
            Shipping to<br />
            <span className="text-voltaic-ink">
              {order?.customer?.name} · {order?.customer?.address}, {order?.customer?.city} {order?.customer?.zip}, {order?.customer?.country}
            </span>
          </div>
        </div>
      </Reveal>

      {/* Referral share */}
      {referral && (
        <Reveal delay={0.2}>
          <div className="mt-6 rounded-3xl border border-voltaic-cyan/40 bg-voltaic-cyan/5 p-6 sm:p-8" data-testid="referral-share-card">
            <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-voltaic-cyan">
              <Share2 size={14} /> Give 10% · Get 10%
            </p>
            <h2 className="mt-2 font-display text-xl font-extrabold uppercase">Share your code, earn discounts</h2>
            <p className="mt-2 text-sm leading-relaxed text-voltaic-muted">
              Friends get 10% off with your code. Every use earns you a 10% credit for your next order.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <span data-testid="referral-code" className="rounded-full border border-dashed border-voltaic-cyan/60 bg-voltaic-cyan/10 px-6 py-2.5 font-mono text-base font-bold tracking-[0.25em] text-voltaic-cyan">
                {referral.code}
              </span>
              <button
                data-testid="referral-copy-button"
                onClick={copyCode}
                className="flex items-center gap-2 rounded-full border border-voltaic-line px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors hover:border-voltaic-cyan hover:text-voltaic-cyan"
              >
                <Copy size={13} /> Copy
              </button>
            </div>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-voltaic-muted" data-testid="referral-stats">
              {referral.uses} referrals · {referral.credits} credit{referral.credits === 1 ? "" : "s"} ready
            </p>
          </div>
        </Reveal>
      )}

      <div className="mt-8 flex flex-wrap justify-center gap-4">
        <Link to="/" data-testid="receipt-home-button" className="rounded-full bg-voltaic-cyan px-7 py-3 font-semibold text-voltaic-bg hover:glow-cyan">Back to home</Link>
        <Link to="/shop" data-testid="receipt-shop-button" className="rounded-full border border-voltaic-linebright px-7 py-3 font-semibold transition-colors hover:border-voltaic-cyan hover:text-voltaic-cyan">Keep shopping</Link>
      </div>
    </main>
  );
}
